import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import api from '../config';
import { loginRequest, MSAL_CLIENT_ID } from '../msalConfig';

const AuthContext = createContext(null);

// Test tenants for dev authentication (multi-tenant isolation testing)
const TENANTS = {
  'tenant-alpha-01': {
    name: 'Alpha Capital Partners',
    user: { name: 'PE Investment Partner', email: 'partner@alphacapital.com' },
  },
  'tenant-beta-02': {
    name: 'Beta Growth Equity',
    user: { name: 'Beta Managing Partner', email: 'partner@betagrowth.com' },
  },
};

const demoUserFor = (tenantId) => {
  const t = TENANTS[tenantId] || TENANTS['tenant-alpha-01'];
  return {
    ...t.user,
    role: 'Caply_Admin',
    tenant: t.name,
    workspace_name: t.name,
    tenant_id: tenantId in TENANTS ? tenantId : 'tenant-alpha-01',
  };
};

export const AuthProvider = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const msalAuthenticated = useIsAuthenticated();
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // null | 'unassigned' | 'failed' — why a signed-in MSAL user has no session
  const [profileError, setProfileError] = useState(null);
  const isDemo = useRef(false);

  const setAuthHeader = (authToken) => {
    if (authToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      localStorage.setItem('caply_auth_token', authToken);
    } else {
      localStorage.removeItem('caply_auth_token');
      delete api.defaults.headers.common['Authorization'];
      delete api.defaults.headers.common['X-Dev-Tenant'];
    }
  };

  const setTenantHeader = (tenantId) => {
    if (tenantId) {
      api.defaults.headers.common['X-Dev-Tenant'] = tenantId;
    } else {
      delete api.defaults.headers.common['X-Dev-Tenant'];
    }
  };

  // ── Real auth: fetch profile from API using a CIAM access token ──
  const loadProfile = async (accessToken) => {
    setAuthHeader(accessToken);
    try {
      const res = await api.get('/api/auth/me');
      setToken(accessToken);
      setUser(res.data);
      isDemo.current = false;
      setProfileError(null);
      return true;
    } catch (err) {
      // 403 = valid CIAM account whose email isn't mapped to a firm yet;
      // anything else (401 expired, network) is a generic failure.
      const status = err?.response?.status;
      setProfileError(status === 403 ? 'unassigned' : 'failed');
      // Don't keep a bearer token that can't load a profile.
      setAuthHeader(null);
      console.warn('Profile fetch failed:', err?.response?.data?.detail || err.message);
      return false;
    }
  };

  // Acquire a token silently (refreshes when expired) and load the profile.
  const acquireAndLoad = async () => {
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) return false;
    try {
      const result = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return await loadProfile(result.accessToken);
    } catch (err) {
      console.warn('Silent token acquisition failed', err);
      return false;
    }
  };

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    // Demo session already active (set by loginDemo before MSAL checked in)
    if (localStorage.getItem('caply_demo') === '1') {
      const tenantId = localStorage.getItem('caply_tenant') || 'tenant-alpha-01';
      // Never let the canned demo user overwrite a profile already loaded
      // from the API (this effect can run again when MSAL settles).
      setUser((prev) => prev || demoUserFor(tenantId));
      setTenantHeader(tenantId);
      setToken('demo-token');
      setLoading(false);
      // Paint instantly from the canned demo user, then take whatever the
      // API says about this member so a demo shows the real role and
      // workspace type (sample workspace, firm workspace, plan).
      setAuthHeader('demo-token');
      api.get('/api/auth/me')
        .then((res) => setUser(res.data))
        .catch(() => {});
      return;
    }

    if (msalAuthenticated) {
      acquireAndLoad().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msalAuthenticated, inProgress]);

  // ── Real login: redirect to the Caply Customer Identity sign-up /
  //    sign-in experience (email + password) ──
  const clearDemoSession = () => {
    localStorage.removeItem('caply_demo');
    localStorage.removeItem('caply_tenant');
    localStorage.removeItem('caply_auth_token');
  };

  const login = async () => {
    // A real (CIAM) sign-in must never restore a leftover demo session.
    clearDemoSession();
    await instance.loginRedirect(loginRequest);
  };

  // First-time visitor: land straight on the create-account form. Entra
  // External ID honours prompt=create, which skips the account picker a
  // returning user would otherwise meet. The chosen plan travels in
  // localStorage (pages/Signup.jsx), so an abandoned sign-up keeps it.
  const signup = async () => {
    clearDemoSession();
    await instance.loginRedirect({ ...loginRequest, prompt: 'create' });
  };

  const logout = async () => {
    localStorage.removeItem('caply_demo');
    localStorage.removeItem('caply_tenant');
    localStorage.removeItem('token');
    localStorage.removeItem('caply_auth_token');
    setToken(null);
    setUser(null);
    setProfileError(null);
    setAuthHeader(null);
    if (msalAuthenticated) {
      await instance.logoutRedirect({ account: instance.getActiveAccount() });
    }
  };

  // ── Demo login (dev/isolation testing) ──
  const loginDemo = (tenantId = 'tenant-alpha-01') => {
    localStorage.setItem('caply_demo', '1');
    localStorage.setItem('caply_tenant', tenantId);
    localStorage.setItem('caply_auth_token', 'demo-token');
    setUser(demoUserFor(tenantId));
    setToken('demo-token');
    setTenantHeader(tenantId);
    // The canned demo user is a placeholder: ask the API who this member
    // really is, so platform staff see their console link without a reload.
    setAuthHeader('demo-token');
    api.get('/api/auth/me')
      .then((res) => setUser(res.data))
      .catch(() => {});
    return true;
  };

  return (
    <AuthContext.Provider
      value={{ token, user, loading, profileError, login, signup, loginDemo, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
